import { Channel } from "discord.js";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import type slack_channels from "../../../types/channels";
import discord_channels from "../../../types/discord_channels";
import { getSupaClient } from "../../lib/supabase";
declare function assertSlackChannels(
  value: unknown
): asserts value is slack_channels[];

function useChannels<Platform extends "slack" | "discord">(
  teamId: string,
  platform: Platform
) {
  const location = useLocation();
  type channel<P> = P extends "slack"
    ? slack_channels
    : P extends "discord"
    ? discord_channels
    : never;
  const [allChannels, setChannels] = useState<channel<Platform>[]>([]);
  const [selectedChannel, setSelectedChannel] =
    useState<channel<Platform> | null>(null);

  const [loading, setLoading] = useState<boolean>(false);

  const supabase = getSupaClient();

  /**
   * Pulls Discord text channels for the current guild excluding
   */
  const pullDiscordChannels = async () =>
    await supabase
      .from<discord_channels>("discord_channels")
      .select("discord_id, name")
      .filter("guild", "eq", teamId)
      .neq("type", "GUILD_VOICE")
      .neq("type", "GUILD_CATEGORY");

  /**
   * Pulls Slack channels for the current team
   */
  const pullSlackChannels = async () =>
    await supabase
      .from<slack_channels>("channels")
      .select("slack_id, name")
      .filter("is_deleted", "eq", false)
      .filter("is_archived", "eq", false)
      .or(`team.eq.${teamId},enterprise.not.is.null`);

  /**
   * useEffect to pull channels from slack or discord and add them to the react state
   */
  useEffect(() => {
    setLoading(true);
    const pullChannels =
      platform === "slack" ? pullSlackChannels : pullDiscordChannels;
    pullChannels().then(({ data, error }) => {
      if (error) {
        console.error(error);
        setLoading(false);
      }
      if (data) {
        setChannels(data as channel<Platform>[]);
        setLoading(false);
      }
    });
  }, []);

  /**
   * useEffect to set selected channel on discord to the channel from the URL paramater channel
   * Only works on discord for now, but could be added for slack.
   */
  useEffect(() => {
    const urlChannel = new URLSearchParams(location.search).get("channel");
    const newChannel = allChannels.find((channel) =>
      "discord_id" in channel ? channel.discord_id === urlChannel : false
    );
    if (newChannel) setSelectedChannel(newChannel as channel<Platform>);
  }, [location, allChannels]);

  return {
    isLoading: loading,
    allChannels,
    setChannels,
    selectedChannel,
    setSelectedChannel,
  };
}

export default useChannels;
